import { useCallback, useMemo } from "react";
import type { BoardRow, QuoteStatusKey } from "@/lib/event-board";
import type { EventStage } from "@/lib/event-pipeline";
import {
  ALL_EVENT_STAGES,
  ALL_QUOTE_STATUSES,
  ALL_SERVICES,
  QUOTE_STATUS_OPTIONS,
  SERVICE_OPTIONS,
  STAGE_OPTION_GROUPS,
  STAGE_SHORTCUTS,
  WHEN_OPTIONS,
  countWith,
  defaultFilters,
  quoteStatusFilterLabel,
  sameSet,
  serviceFilterLabel,
  stageFilterLabel,
  toggleOption,
  type EventFilters,
  type FilterOption,
  type MultiFilterKey,
} from "@/lib/event-filters";
import type { ServiceType } from "@/types/quotes";
import { FilterPopover, type FilterPopoverOption } from "./FilterPopover";

interface EventsFilterBarProps {
  rows: BoardRow[];
  filters: EventFilters;
  today: Date;
  isDefault: boolean;
  updateFilters: (patch: Partial<EventFilters>) => void;
  clearFilters: () => void;
  clearIncomplete: () => void;
}

function withCounts<K extends MultiFilterKey>(
  rows: BoardRow[],
  f: EventFilters,
  key: K,
  options: FilterOption<EventFilters[K][number]>[],
  today: Date,
): FilterPopoverOption<EventFilters[K][number]>[] {
  const picked = f[key] as readonly string[];
  return options.map((o) => ({ ...o, checked: picked.includes(o.key), count: countWith(rows, f, key, o.key, today) }));
}

function RemovableChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-accent bg-[hsl(var(--ev-accent-soft))] py-[3px] pl-2.5 pr-1.5 text-[13px] font-semibold">
      {label}
      <button
        type="button"
        aria-label={`Remove ${label}`}
        onClick={onRemove}
        className="px-1 text-[15px] leading-none text-muted-foreground"
      >
        ×
      </button>
    </span>
  );
}

/** The When presets and, for Custom…, the From / To dates. */
function WhenFilter({ filters: f, updateFilters }: Pick<EventsFilterBarProps, "filters" | "updateFilters">) {
  return (
    <>
      <span className="text-[11.5px] font-bold uppercase tracking-[.08em] text-muted-foreground">When</span>
      <div
        role="group"
        aria-label="Event date"
        className="inline-flex flex-wrap overflow-hidden rounded-lg border bg-card"
      >
        {WHEN_OPTIONS.map((w) => (
          <button
            key={w.key}
            type="button"
            aria-pressed={f.when === w.key}
            onClick={() => updateFilters({ when: w.key })}
            className="border-r px-3 py-1.5 text-[13.5px] last:border-r-0 aria-pressed:bg-foreground aria-pressed:font-semibold aria-pressed:text-background"
          >
            {w.label}
          </button>
        ))}
      </div>
      {f.when === "custom" && (
        <span className="inline-flex items-center gap-1.5 text-[13px]">
          <input
            type="date"
            aria-label="From"
            value={f.from}
            onChange={(e) => updateFilters({ from: e.target.value })}
            className="rounded-[7px] border bg-card px-2 py-1"
          />
          to
          <input
            type="date"
            aria-label="To"
            value={f.to}
            onChange={(e) => updateFilters({ to: e.target.value })}
            className="rounded-[7px] border bg-card px-2 py-1"
          />
        </span>
      )}
    </>
  );
}

/** Each dropdown's options with counts, its toggle and its shortcuts. */
function useFilterControls(
  rows: BoardRow[],
  f: EventFilters,
  today: Date,
  updateFilters: EventsFilterBarProps["updateFilters"],
) {
  const options = useMemo(
    () => ({
      services: withCounts(rows, f, "services", SERVICE_OPTIONS, today),
      quoteStatuses: withCounts(rows, f, "quoteStatuses", QUOTE_STATUS_OPTIONS, today),
      stageGroups: STAGE_OPTION_GROUPS.map((g) => ({
        label: g.label,
        options: withCounts(rows, f, "stages", g.options, today),
      })),
    }),
    [rows, f, today],
  );

  const toggleService = useCallback(
    (k: ServiceType) => updateFilters({ services: toggleOption(f.services, k, ALL_SERVICES) }),
    [f.services, updateFilters],
  );
  const toggleStage = useCallback(
    (k: EventStage) => updateFilters({ stages: toggleOption(f.stages, k, ALL_EVENT_STAGES) }),
    [f.stages, updateFilters],
  );
  const toggleQuoteStatus = useCallback(
    (k: QuoteStatusKey) => updateFilters({ quoteStatuses: toggleOption(f.quoteStatuses, k, ALL_QUOTE_STATUSES) }),
    [f.quoteStatuses, updateFilters],
  );

  const shortcuts = useMemo(
    () => ({
      services: [
        { label: "All", onSelect: () => updateFilters({ services: [...ALL_SERVICES] }) },
        { label: "None", onSelect: () => updateFilters({ services: [] }) },
      ],
      quoteStatuses: [
        { label: "All", onSelect: () => updateFilters({ quoteStatuses: [...ALL_QUOTE_STATUSES] }) },
        { label: "None", onSelect: () => updateFilters({ quoteStatuses: [] }) },
      ],
      stages: STAGE_SHORTCUTS.map((s) => ({
        label: s.label,
        onSelect: () => updateFilters({ stages: [...s.stages] }),
      })),
    }),
    [updateFilters],
  );

  const d = useMemo(() => defaultFilters(today), [today]);

  return { options, shortcuts, toggleService, toggleStage, toggleQuoteStatus, d };
}

/** When, Service, Event status and Quote status, plus the extra chips and Clear filters (#1557). */
export function EventsFilterBar(props: EventsFilterBarProps) {
  const { rows, filters: f, today, isDefault, updateFilters, clearFilters, clearIncomplete } = props;

  const { options, shortcuts, toggleService, toggleStage, toggleQuoteStatus, d } = useFilterControls(
    rows,
    f,
    today,
    updateFilters,
  );

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 rounded-[10px] bg-muted px-3 py-2.5">
      <WhenFilter filters={f} updateFilters={updateFilters} />
      <FilterPopover
        label="Service"
        value={serviceFilterLabel(f.services)}
        set={!sameSet(f.services, d.services)}
        shortcuts={shortcuts.services}
        groups={[{ options: options.services }]}
        onToggle={toggleService}
      />
      <FilterPopover
        label="Event status"
        value={stageFilterLabel(f.stages)}
        set={!sameSet(f.stages, d.stages)}
        shortcuts={shortcuts.stages}
        groups={options.stageGroups}
        onToggle={toggleStage}
      />
      <FilterPopover
        label="Quote status"
        value={quoteStatusFilterLabel(f.quoteStatuses)}
        set={!sameSet(f.quoteStatuses, d.quoteStatuses)}
        shortcuts={shortcuts.quoteStatuses}
        groups={[{ options: options.quoteStatuses }]}
        onToggle={toggleQuoteStatus}
      />
      {f.noReply && <RemovableChip label="No reply 3+ days" onRemove={() => updateFilters({ noReply: false })} />}
      {f.incomplete && <RemovableChip label="Incomplete leads" onRemove={clearIncomplete} />}
      {!isDefault && (
        <button type="button" onClick={clearFilters} className="px-0.5 py-1 text-[13.5px] font-bold text-accent">
          Clear filters
        </button>
      )}
    </div>
  );
}
